/**
 * Type-Safe Translation System
 * All translation keys are type-checked at compile time
 */

import type { Translations, TranslationKey, Language } from '@/types/branding.types'
import { branding } from './branding'

/**
 * All translations for the application
 * Keys must match TranslationKey type
 */
export const translations: Translations = {
  ru: {
    // General
    welcome_message: 'AI-ассистент для аналитики кода и документации',
    disclaimer:
      'Ответы генерируются ИИ и могут содержать неточности. Всегда проверяйте важную информацию.',
    backend_unavailable:
      'Не удается подключиться к серверу. Проверьте, что бэкенд запущен на http://localhost:8001',

    // Chat
    chat_input_placeholder: 'Задайте вопрос о коде или документации...',
    send_button: 'Отправить',
    copy_answer: 'Скопировать ответ',
    copy_diagram_code: 'Скопировать код диаграммы',
    clear_chat_button: '🗑️ Очистить чат',
    stop_generation: '⏹️ Остановить',
    retry_last: '🔄 Повторить',

    // Settings
    settings_header: '⚙️ Настройки',
    project_label: 'Проект',
    scenario_label: 'Сценарий',
    search_mode_label: 'Режим поиска',
    reindex_button: '🔄 Переиндексировать',
    reindex_help: 'Обновить индекс кода и документации',
    reindexing_in_progress: '⏳ Идет переиндексация...',
    reindex_started: '✅ Индексация запущена успешно',

    // Scenarios
    scenario_default: '🔍 Общий поиск',
    scenario_impact_analysis: '📊 Анализ влияния',
    scenario_requirements_validation: '✅ Проверка требований',
    scenario_code_documentation: '📝 Документация кода',
    scenario_template_generation: '📋 Генерация по шаблону',
    scenario_mermaid_generation: '📊 Генерация диаграмм',

    // Search modes
    search_mode_smart: '🧠 Умный (автовыбор)',
    search_mode_direct: '⚡ Прямой поиск',
    search_mode_agent: '🤖 Агентный режим',

    // Files
    files_header: '📎 Файлы',
    file_upload_label: 'Прикрепить файлы',
    file_upload_help: 'Поддерживаются: PDF, DOCX, изображения, код',
    file_attached: 'Прикреплено файлов: {count}',
    file_processing: 'Обработка {filename}...',
    clear_files_button: '🗑️ Очистить файлы',
    show_files: 'Показать файлы',
    upload_error: 'Ошибка загрузки: {error}',

    // Sources
    sources: '📚 Источники ({count})',
    diagram_code: '📊 Код диаграммы',

    // Agent
    agent_actions: '🤖 Действия агента',
    agent_working: 'Агент работает...',
    agent_start: '🚀 **Агент начал работу**',
    agent_planning: '📋 **Планирование сбора информации...**',
    agent_plan_created: '✅ **План создан**: {count} шагов',
    agent_plan_failed: '❌ **Ошибка планирования**: {error}',
    agent_completeness: '{icon} **Полнота информации**: {score}%',
    agent_iteration: '### 🔄 Итерация {iteration}',
    agent_thinking: '💭 **Размышление...**',
    agent_thought: '💭 {thought}',
    agent_finish: '\n✅ **Завершено**: {reason}',
    agent_complete: '🎉 **Готово** за {iterations} итераций. Источников: {sources}',
    agent_confirmation: '⚠️ {message}',

    // Errors
    connection_error: 'Ошибка соединения: {error}',
    timeout_error: 'Превышено время ожидания. Попробуйте упростить запрос.',
    template_loading: 'Загрузка шаблона...',
    template_load_error: 'Не удалось загрузить шаблон',

    // Warnings
    no_projects_warning: '⚠️ Проекты не найдены. Создайте проект в настройках.',
    no_templates_warning: '⚠️ Шаблоны не найдены для этого проекта.',
    template_select: 'Выберите шаблон',

    // New - Iteration 3
    no_messages: 'Нет сообщений. Начните диалог!',
    clear_chat: 'Очистить чат',
    chat_cleared: 'Чат очищен',
    stop: 'Стоп',
    send: 'Отправить',
    attach_file: 'Прикрепить файл',
    file_removed: 'Файл удален',
    file_uploaded_success: 'Файл {name} загружен',
    file_upload_error: 'Ошибка загрузки файла {name}',
    source_document: 'Документ',
    generation_stopped: 'Генерация остановлена',

    // Theme
    theme_light: 'Светлая тема',
    theme_dark: 'Темная тема',

    // Loading
    generating_response: 'Генерирую ответ...',

    // Agent activity
    agent_activity_title: 'Процесс работы агента',
    agent_activity_started: 'Агент запущен',
    agent_activity_iteration: 'Итерация',
    agent_activity_thinking: 'Размышление',
    agent_activity_thought: 'Мысль',
    agent_activity_success: 'Успешно',
    agent_activity_error: 'Ошибка',
    agent_activity_found_documents: 'найдено {count} документов',
    agent_activity_steps: '{count} шагов',
    agent_activity_action: 'Действие',
    agent_activity_observation: 'Наблюдение',
    agent_activity_planning_start: 'Начало планирования',
    agent_activity_planning_complete: 'Планирование завершено',
    agent_activity_complete: 'Агент завершил работу',

    // Project settings
    create_project: 'Создать проект',
    edit_project: 'Редактировать проект',
    project_id: 'ID проекта',
    project_name: 'Название проекта',
    description: 'Описание',
    enabled: 'Включен',
    general_settings: 'Основные',
    resources: 'Ресурсы',
    cancel: 'Отмена',
    save: 'Сохранить',
    project_created: 'Проект создан успешно',
    project_updated: 'Проект обновлен успешно',

    // Resources
    gitlab_repos: 'GitLab репозитории',
    github_repos: 'GitHub репозитории',
    confluence_spaces: 'Confluence пространства',
    jira_projects: 'Jira проекты',
    add_gitlab_repo: 'Добавить GitLab репозиторий',
    add_github_repo: 'Добавить GitHub репозиторий',
    add_confluence_space: 'Добавить Confluence пространство',
    add_jira_project: 'Добавить Jira проект',
    gitlab_url: 'GitLab URL (например, https://gitlab.com)',
    github_url: 'GitHub URL (например, https://github.com)',
    confluence_url: 'Confluence URL',
    jira_url: 'Jira URL',
    project_path_placeholder: 'Путь к проекту (например, group/project)',
    access_token: 'Токен доступа',
    api_token: 'API токен',
    branch: 'Ветка (например, main)',
    space_key: 'Ключ пространства',
    project_key: 'Ключ проекта',
    user_email: 'Email пользователя (опционально для PAT)',
    cloud_instance: 'Cloud инстанс',
    remove: 'Удалить',

    // Copy functionality
    copy_message: 'Копировать сообщение',
    message_copied: 'Сообщение скопировано',
    copy_failed: 'Ошибка копирования',
    copied_to_clipboard: 'Скопировано в буфер обмена!',
    copied: 'Скопировано!',
    copy_to_clipboard: 'Копировать в буфер',
    review_and_edit_before_copying: 'Просмотрите и отредактируйте содержимое перед копированием:',

    // Source tags
    source_tags: 'Источники',
    all_sources: 'Все источники',
    tag_placeholder: 'Тег (напр. backend, frontend, docs)',
    default_tags: 'Теги по умолчанию',
    default_tags_placeholder: 'Выберите теги, включённые по умолчанию',
    no_tags_hint: 'Сначала добавьте теги к ресурсам',

    // Demo mode
    demo_select_query: 'Выберите один из примеров запросов:',
    demo_limit_reached:
      'Это демо-версия. Вы достигли лимита запросов (4). Для получения полного доступа свяжитесь с нами.',
    demo_contact_us: 'Связаться с нами',
  },

  en: {
    // General
    welcome_message: 'AI Assistant for Code and Documentation Analysis',
    disclaimer:
      'Answers are generated by AI and may contain inaccuracies. Always verify important information.',
    backend_unavailable:
      'Cannot connect to server. Make sure backend is running on http://localhost:8001',

    // Chat
    chat_input_placeholder: 'Ask a question about code or documentation...',
    send_button: 'Send',
    copy_answer: 'Copy answer',
    copy_diagram_code: 'Copy diagram code',
    clear_chat_button: '🗑️ Clear chat',
    stop_generation: '⏹️ Stop',
    retry_last: '🔄 Retry',

    // Settings
    settings_header: '⚙️ Settings',
    project_label: 'Project',
    scenario_label: 'Scenario',
    search_mode_label: 'Search Mode',
    reindex_button: '🔄 Reindex',
    reindex_help: 'Update code and documentation index',
    reindexing_in_progress: '⏳ Reindexing in progress...',
    reindex_started: '✅ Indexing started successfully',

    // Scenarios
    scenario_default: '🔍 General Search',
    scenario_impact_analysis: '📊 Impact Analysis',
    scenario_requirements_validation: '✅ Requirements Validation',
    scenario_code_documentation: '📝 Code Documentation',
    scenario_template_generation: '📋 Template Generation',
    scenario_mermaid_generation: '📊 Diagram Generation',

    // Search modes
    search_mode_smart: '🧠 Smart (auto)',
    search_mode_direct: '⚡ Direct Search',
    search_mode_agent: '🤖 Agent Mode',

    // Files
    files_header: '📎 Files',
    file_upload_label: 'Attach files',
    file_upload_help: 'Supported: PDF, DOCX, images, code',
    file_attached: 'Attached files: {count}',
    file_processing: 'Processing {filename}...',
    clear_files_button: '🗑️ Clear files',
    show_files: 'Show files',
    upload_error: 'Upload error: {error}',

    // Sources
    sources: '📚 Sources ({count})',
    diagram_code: '📊 Diagram code',

    // Agent
    agent_actions: '🤖 Agent Actions',
    agent_working: 'Agent is working...',
    agent_start: '🚀 **Agent started**',
    agent_planning: '📋 **Planning information gathering...**',
    agent_plan_created: '✅ **Plan created**: {count} steps',
    agent_plan_failed: '❌ **Planning error**: {error}',
    agent_completeness: '{icon} **Information completeness**: {score}%',
    agent_iteration: '### 🔄 Iteration {iteration}',
    agent_thinking: '💭 **Thinking...**',
    agent_thought: '💭 {thought}',
    agent_finish: '\n✅ **Finished**: {reason}',
    agent_complete: '🎉 **Done** in {iterations} iterations. Sources: {sources}',
    agent_confirmation: '⚠️ {message}',

    // Errors
    connection_error: 'Connection error: {error}',
    timeout_error: 'Request timeout. Try simplifying your query.',
    template_loading: 'Loading template...',
    template_load_error: 'Failed to load template',

    // Warnings
    no_projects_warning: '⚠️ No projects found. Create a project in settings.',
    no_templates_warning: '⚠️ No templates found for this project.',
    template_select: 'Select template',

    // New - Iteration 3
    no_messages: 'No messages yet. Start a conversation!',
    clear_chat: 'Clear chat',
    chat_cleared: 'Chat cleared',
    stop: 'Stop',
    send: 'Send',
    attach_file: 'Attach file',
    file_removed: 'File removed',
    file_uploaded_success: 'File {name} uploaded',
    file_upload_error: 'Error uploading file {name}',
    source_document: 'Document',
    generation_stopped: 'Generation stopped',

    // Theme
    theme_light: 'Light theme',
    theme_dark: 'Dark theme',

    // Loading
    generating_response: 'Generating response...',

    // Agent activity
    agent_activity_title: 'Agent Work Process',
    agent_activity_started: 'Agent started',
    agent_activity_iteration: 'Iteration',
    agent_activity_thinking: 'Thinking',
    agent_activity_thought: 'Thought',
    agent_activity_success: 'Success',
    agent_activity_error: 'Error',
    agent_activity_found_documents: 'found {count} documents',
    agent_activity_steps: '{count} steps',
    agent_activity_action: 'Action',
    agent_activity_observation: 'Observation',
    agent_activity_planning_start: 'Planning started',
    agent_activity_planning_complete: 'Planning completed',
    agent_activity_complete: 'Agent completed work',

    // Project settings
    create_project: 'Create Project',
    edit_project: 'Edit Project',
    project_id: 'Project ID',
    project_name: 'Project Name',
    description: 'Description',
    enabled: 'Enabled',
    general_settings: 'General',
    resources: 'Resources',
    cancel: 'Cancel',
    save: 'Save',
    project_created: 'Project created successfully',
    project_updated: 'Project updated successfully',

    // Resources
    gitlab_repos: 'GitLab Repositories',
    github_repos: 'GitHub Repositories',
    confluence_spaces: 'Confluence Spaces',
    jira_projects: 'Jira Projects',
    add_gitlab_repo: 'Add GitLab Repository',
    add_github_repo: 'Add GitHub Repository',
    add_confluence_space: 'Add Confluence Space',
    add_jira_project: 'Add Jira Project',
    gitlab_url: 'GitLab URL (e.g., https://gitlab.com)',
    github_url: 'GitHub URL (e.g., https://github.com)',
    confluence_url: 'Confluence URL',
    jira_url: 'Jira URL',
    project_path_placeholder: 'Project path (e.g., owner/repo)',
    access_token: 'Access Token',
    api_token: 'API Token',
    branch: 'Branch (e.g., main)',
    space_key: 'Space Key',
    project_key: 'Project Key',
    user_email: 'User Email (optional for PAT)',
    cloud_instance: 'Cloud Instance',
    remove: 'Remove',

    // Copy functionality
    copy_message: 'Copy message',
    message_copied: 'Message copied',
    copy_failed: 'Copy failed',
    copied_to_clipboard: 'Copied to clipboard!',
    copied: 'Copied!',
    copy_to_clipboard: 'Copy to Clipboard',
    review_and_edit_before_copying: 'Review and edit the content before copying:',

    // Source tags
    source_tags: 'Sources',
    all_sources: 'All sources',
    tag_placeholder: 'Tag (e.g. backend, frontend, docs)',
    default_tags: 'Default Tags',
    default_tags_placeholder: 'Select tags enabled by default',
    no_tags_hint: 'Add tags to resources first',

    // Demo mode
    demo_select_query: 'Select one of the example queries:',
    demo_limit_reached:
      'This is a live demo version. You have reached the query limit (4). To get full access, please contact us.',
    demo_contact_us: 'Contact Us',
  },
}

/**
 * Get translation for a key with optional parameters
 * @param key - Translation key (type-safe)
 * @param params - Parameters to replace in translation string
 * @returns Translated string
 */
export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  const language: Language = branding.language
  let text = translations[language][key]

  // Replace parameters in format {param}
  if (params) {
    Object.entries(params).forEach(([paramKey, value]) => {
      text = text.replace(`{${paramKey}}`, String(value))
    })
  }

  return text
}

/**
 * Get translation without parameters (convenience function)
 */
export function tr(key: TranslationKey): string {
  return t(key)
}
